import { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";

function validStored(stored) {
    return stored !== null && stored !== "null" && stored !== undefined && stored !== "" && stored !== "undefined";
}

export default function useStorageState(key, initialValue, closeFunc) {
    const builtKey = useMemo(() => `kodeo.${key}`, [key]);
    const prevKeyRef = useRef(builtKey);
    const mountedKeyRef = useRef(builtKey);
    const hasRunCleanupRef = useRef(false);

    const runCloseFunc = (targetKey) => {
        try {
            const stored = localStorage.getItem(targetKey);
            const parsed = JSON.parse(stored);
            const endVal = closeFunc?.(parsed);
            if (endVal !== undefined) {
                localStorage.setItem(targetKey, JSON.stringify(endVal));
            }
        } catch { }
    };

    const [value, setValue] = useState(() => {
        if (typeof window === "undefined") return initialValue;
        try {
            const saved = localStorage.getItem(builtKey);
            return validStored(saved) ? JSON.parse(saved) : initialValue;
        } catch {
            return initialValue;
        }
    });

    useLayoutEffect(() => {
        if (prevKeyRef.current !== builtKey) {
            runCloseFunc(prevKeyRef.current); // cleanup old
            hasRunCleanupRef.current = false; // Reset flag for new key
            try {
                const stored = localStorage.getItem(builtKey);
                setValue(validStored(stored) ? JSON.parse(stored) : initialValue);
            } catch {
                setValue(initialValue);
            }
            prevKeyRef.current = builtKey;
            mountedKeyRef.current = builtKey; // Update mounted key too
        }
    }, [builtKey, initialValue]);

    useEffect(() => {
        try {
            localStorage.setItem(builtKey, JSON.stringify(value));
        } catch { }
    }, [builtKey, value]);

    useEffect(() => () => {
        // Only run cleanup on unmount if we haven't already cleaned up this key
        if (!hasRunCleanupRef.current) {
            hasRunCleanupRef.current = true;
            runCloseFunc(mountedKeyRef.current);
        }
    }, []);

    return [value, setValue];
}